import { type ClassValue, clsx } from "clsx";
import { useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function must<T>(val: T) {
	if (!val) {
		throw new Error("Expected value to be defined");
	}
	return val;
}

export function getInitials(name: string): string {
	const parts = name.trim().split(" ");
	if (parts.length >= 2) {
		return (parts[0][0] + parts.at(-1)?.[0]).toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
}

export type Mutable<T> = {
	-readonly [K in keyof T]: T[K];
};

export type Readonly<T> = {
	readonly [K in keyof T]: T[K];
};

export type MutableRow<T> = {
	-readonly [K in keyof T]: T[K] extends ReadonlyArray<infer U> ? U[] : T[K];
};

export type Expand<T> = { [K in keyof T]: T[K] };

export const decodeJwt = (token: string) => {
	const decode = (data: string) => {
		if (typeof Buffer === "undefined") {
			return atob(data);
		}
		return Buffer.from(data, "base64").toString();
	};
	const parts = token
		.split(".")
		.map((part) => decode(part.replace(/-/g, "+").replace(/_/g, "/")));

	return JSON.parse(parts[1]);
};

/**
 * Returns a function that calls all of its arguments.
 */
export function callAll<Args extends unknown[]>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => {
		for (const fn of fns) {
			fn?.(...args);
		}
	};
}

/**
 * Simple debounce implementation
 */
function debounce<Callback extends (...args: Parameters<Callback>) => void>(
	fn: Callback,
	delay: number,
) {
	let timer: ReturnType<typeof setTimeout> | null = null;
	return (...args: Parameters<Callback>) => {
		if (timer) {
			clearTimeout(timer);
		}
		timer = setTimeout(() => {
			fn(...args);
		}, delay);
	};
}

/**
 * Debounce a callback function
 */
export function useDebounce<
	Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
	const callbackRef = useRef(callback);
	callbackRef.current = callback;
	return useMemo(
		() =>
			debounce(
				(...args: Parameters<Callback>) => callbackRef.current(...args),
				delay,
			),
		[delay],
	);
}
